import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getResultState, getUIState } from '../../store/slices/selectors'

import { useLazyEffect } from '../../../../utils/hooks'
import { PANE_HEADER } from '../../../../utils/constants'
import { MESH_MEMBERS } from '../../common/constants'

import Tooltip from 'react-bootstrap/Tooltip'
import { FormGroup, Checkbox, Popper, Button, LoadingIndicator } from '../../../../components'
import { RenderIf } from '../../../../utils/fns'

const Illustration = (props) => {
  const modelRef = useRef()

  const graphicsData = useSelector((state) => getUIState(state).graphicsData)
  const selectedModel = useSelector((state) => getResultState(state).selectedModel)

  const [memberEnabled, setMemberEnabled] = useState({
    [MESH_MEMBERS.WALL]: true,
    [MESH_MEMBERS.ROOF]: true,
    [MESH_MEMBERS.DIMENSIONS]: true,
    [MESH_MEMBERS.CONNECTORS]: false,
  })

  const [loading, setLoading] = useState(false)

  useLazyEffect(
    () => {
      async function createConnection() {
        setLoading(true)
        const model = modelRef.current

        try {
          if (model) {
            const isRenderConnectors = Boolean(selectedModel)
            const psuedoTarget = {
              target: { name: MESH_MEMBERS.CONNECTORS, checked: isRenderConnectors },
            }

            if (isRenderConnectors) await model.createConnectors()

            handleConnectorsEnabled(psuedoTarget)
          }

          setLoading(false)
        } catch (error) {
          console.log(error)
        } finally {
          setLoading(false)
        }
      }

      createConnection()
    },
    [selectedModel],
    300,
  )

  const handleResetCameraView = () => {
    if (modelRef.current) {
      modelRef.current.handleResetCameraView()
    }
  }

  const handleMemberEnabled = (e) => {
    const { name, checked } = e.target
    if (modelRef.current) {
      const model = modelRef.current

      if (memberEnabled[MESH_MEMBERS.CONNECTORS]) {
        model.handleMemberEnabled(MESH_MEMBERS.CONNECTORS, false)
        setMemberEnabled((prev) => ({ ...prev, [MESH_MEMBERS.CONNECTORS]: false }))
      }

      model.handleMemberEnabled(name, checked)
      setMemberEnabled((prev) => ({ ...prev, [name]: checked }))

      handleResetCameraView()
    }
  }

  const handleConnectorsEnabled = (e) => {
    const { name, checked } = e.target

    if (modelRef.current) {
      const model = modelRef.current

      Object.keys(memberEnabled)
        .slice(0, 3)
        .forEach((member) => {
          model.handleMemberEnabled(member, !checked)
          setMemberEnabled((prev) => ({ ...prev, [member]: !checked }))
        })

      model.handleMemberEnabled(name, checked)
      setMemberEnabled((prev) => ({ ...prev, [name]: checked }))

      handleResetCameraView()
    }
  }

  return (
    <div className="outputSectionWrapper modelViewer3D" {...props}>
      <div className="paneHeader">
        <div className="paneHeader__staticHeading">{PANE_HEADER['3D']}</div>
      </div>

      <div className="illustrationSection">
        {RenderIf(loading, <LoadingIndicator />)}
        {graphicsData && (
          <bp-rws
            ref={modelRef}
            wall={JSON.stringify(graphicsData.wall)}
            roof={JSON.stringify(graphicsData.roof)}
            connectors={JSON.stringify(graphicsData.connectors)}
          />
        )}
        <div className="illustrationSection__actions">
          <div className="d-flex flex-column gap-y-4 p-4">
            <div>
              <Popper
                trigger={['hover', 'focus']}
                delay={0}
                overlay={
                  <Tooltip>
                    <p className="my-0">Reset View</p>
                  </Tooltip>
                }
              >
                <Button variant="" className="resetView-btn" onClick={handleResetCameraView}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8ZM20.94 11C20.48 6.83 17.17 3.52 13 3.06V1H11V3.06C6.83 3.52 3.52 6.83 3.06 11H1V13H3.06C3.52 17.17 6.83 20.48 11 20.94V23H13V20.94C17.17 20.48 20.48 17.17 20.94 13H23V11H20.94ZM12 19C8.13 19 5 15.87 5 12C5 8.13 8.13 5 12 5C15.87 5 19 8.13 19 12C19 15.87 15.87 19 12 19Z"
                      fill="#623C11"
                    />
                  </svg>
                </Button>
              </Popper>
            </div>
            <div>
              <p className="mt-0 mb-2">Object Visibility</p>
              <div className="d-flex flex-column gap-y-1">
                <FormGroup>
                  <Checkbox
                    name={MESH_MEMBERS.WALL}
                    label="Wall"
                    checked={memberEnabled[MESH_MEMBERS.WALL]}
                    onChange={handleMemberEnabled}
                  />
                </FormGroup>
                <FormGroup>
                  <Checkbox
                    name={MESH_MEMBERS.ROOF}
                    label="Roof"
                    checked={memberEnabled[MESH_MEMBERS.ROOF]}
                    onChange={handleMemberEnabled}
                  />
                </FormGroup>
                <FormGroup>
                  <Checkbox
                    name={MESH_MEMBERS.DIMENSIONS}
                    label="Dimensions"
                    checked={memberEnabled[MESH_MEMBERS.DIMENSIONS]}
                    onChange={handleMemberEnabled}
                  />
                </FormGroup>
                {selectedModel ? (
                  <FormGroup>
                    <Checkbox
                      name={MESH_MEMBERS.CONNECTORS}
                      label="Connection"
                      checked={memberEnabled[MESH_MEMBERS.CONNECTORS]}
                      onChange={handleConnectorsEnabled}
                    />
                  </FormGroup>
                ) : (
                  <Popper
                    trigger={['hover', 'focus']}
                    rootClose
                    delay={0}
                    overlay={
                      <Tooltip>
                        <p className="my-0">Make a selection in the output</p>
                      </Tooltip>
                    }
                  >
                    <FormGroup>
                      <Checkbox
                        name={MESH_MEMBERS.CONNECTORS}
                        label="Connection"
                        checked={memberEnabled[MESH_MEMBERS.CONNECTORS]}
                        onChange={handleConnectorsEnabled}
                        disabled={!selectedModel}
                      />
                    </FormGroup>
                  </Popper>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Illustration
